/**
 * aeterna/bridge.ts — Game State → Neural Cortex Bridge
 *
 * Translates the game's RoomLayout + TimeState into the neural cortex's
 * expected input formats. This is the missing link between the room the
 * player sees and the room the Aeterna perceives.
 *
 * Without this, the HDC module operates on an empty scene and
 * crossmodal text↔room coherence is always 0.
 *
 * With this, the Aeterna can say: "your room feels different today,
 * did you move the bookshelf?"
 */

import type { Room, TimeState, FurniturePlacement } from "../engine/state.js";
import type { RoomContext } from "./neural/hdc.js";
import type { RoomFeatures } from "./neural/crossmodal.js";

// ─── Time Slot Mapping ──────────────────────────────────────────────────────

function gameHourToTimeSlot(hour: number): string {
  if (hour < 5)  return "night";
  if (hour < 8)  return "dawn";
  if (hour < 12) return "morning";
  if (hour < 14) return "midday";
  if (hour < 17) return "afternoon";
  if (hour < 20) return "evening";
  if (hour < 22) return "dusk";
  return "night";
}

// ─── Furniture → Activity Mapping ───────────────────────────────────────────

// Maps furniture types to what "activities" they represent in the room.
// The HDC uses these for sequence encoding of recent interactions.
const FURNITURE_ACTIVITIES: Record<string, string> = {
  bed: "resting",
  desk: "working",
  bookshelf: "reading",
  record_player: "listening",
  crystal_ball: "contemplating",
  potted_fern: "gardening",
  potted_plant: "gardening",
  fish_tank: "watching",
  rug_woven: "comfort",
  floor_lamp: "lighting",
  chair_wooden: "sitting",
  coffee_table: "gathering",
  candle: "ambiance",
  fireplace: "warming",
  telescope: "stargazing",
  painting: "appreciating",
  piano: "playing_music",
  easel: "painting",
  clock: "timekeeping",
};

// ─── Bridge Functions ───────────────────────────────────────────────────────

/**
 * Convert game room state + time into the HDC's RoomContext.
 * This populates the scene hypervector so the Aeterna can reason
 * about the room holographically.
 */
export function buildRoomContext(
  room: Room,
  time: TimeState,
  recentActivities: string[] = [],
  visitors = 0,
  playerMood?: string,
): RoomContext {
  return {
    furnitureTypes: room.furniture.map((f: FurniturePlacement) => f.type),
    timeSlot: gameHourToTimeSlot(time.gameHour),
    season: time.season,
    warmth: room.mood.warmth,
    energy: room.mood.energy,
    mystery: room.mood.mystery,
    nature: room.mood.nature,
    noise: room.mood.noise,
    recentActivities,
    playerMood,
    visitors,
  };
}

// ─── Furniture Category Sets ────────────────────────────────────────────────

const SEATING = new Set(["bed", "chair_wooden", "couch", "cushion", "bench", "stool"]);
const LIGHTING = new Set(["floor_lamp", "candle", "desk_lamp", "chandelier", "lantern", "torch"]);
const DECOR = new Set(["painting", "sculpture", "vase", "clock", "mirror", "rug_woven", "tapestry"]);
const STORAGE = new Set(["bookshelf", "chest", "wardrobe", "cabinet", "shelf", "desk"]);
const NATURE_ITEMS = new Set(["potted_fern", "potted_plant", "flower_pot", "terrarium", "fish_tank", "birdcage"]);
const ENTERTAINMENT = new Set(["record_player", "piano", "easel", "crystal_ball", "arcade", "telescope", "globe"]);

/**
 * Convert game room state into the CrossModal binder's RoomFeatures.
 * This enables text↔room coherence detection.
 */
export function buildRoomFeatures(room: Room, time: TimeState, visitors = 0): RoomFeatures {
  const types = room.furniture.map((f: FurniturePlacement) => f.type);

  let seatingCount = 0, lightingCount = 0, decorCount = 0;
  let storageCount = 0, natureCount = 0, entertainmentCount = 0;

  for (const type of types) {
    if (SEATING.has(type)) seatingCount++;
    if (LIGHTING.has(type)) lightingCount++;
    if (DECOR.has(type)) decorCount++;
    if (STORAGE.has(type)) storageCount++;
    if (NATURE_ITEMS.has(type)) natureCount++;
    if (ENTERTAINMENT.has(type)) entertainmentCount++;
  }

  // Compute spatial features
  const area = room.width * room.height;
  const density = Math.min(1, room.furniture.length / Math.max(1, area * 0.5));

  // Symmetry: compare furniture distribution left vs right, top vs bottom
  const midX = room.width / 2;
  const midY = room.height / 2;
  let leftCount = 0, rightCount = 0, topCount = 0, bottomCount = 0;
  let centerDist = 0;
  for (const f of room.furniture) {
    if (f.tileX < midX) leftCount++; else rightCount++;
    if (f.tileY < midY) topCount++; else bottomCount++;
    centerDist += Math.sqrt((f.tileX - midX) ** 2 + (f.tileY - midY) ** 2);
  }
  const total = Math.max(1, room.furniture.length);
  const lrBalance = 1 - Math.abs(leftCount - rightCount) / total;
  const tbBalance = 1 - Math.abs(topCount - bottomCount) / total;
  const symmetry = (lrBalance + tbBalance) / 2;
  const maxDist = Math.sqrt(midX ** 2 + midY ** 2);
  const centeredness = 1 - (centerDist / total) / Math.max(1, maxDist);

  return {
    warmth: room.mood.warmth,
    energy: room.mood.energy,
    mystery: room.mood.mystery,
    nature: room.mood.nature,
    noise: room.mood.noise,
    seatingCount,
    lightingCount,
    decorCount,
    storageCount,
    natureCount,
    entertainmentCount,
    furnitureDensity: density,
    symmetry,
    centeredness,
    timeOfDay: time.gameHour / 24,
    seasonPhase: ["spring", "summer", "autumn", "winter"].indexOf(time.season) / 4,
    hasVisitors: visitors > 0,
    recentActivityLevel: Math.min(1, room.furniture.filter((f: FurniturePlacement) => {
      const s = f.state as Record<string, unknown>;
      return s.on === true || s.lit === true || s.playing === true;
    }).length / Math.max(1, total)),
  };
}

/**
 * Derive recent activities from the room's furniture state.
 * Furniture with active states (lamp: on, plant: watered recently)
 * suggests recent player engagement.
 */
export function deriveRecentActivities(room: Room): string[] {
  const activities: string[] = [];

  for (const f of room.furniture) {
    const activity = FURNITURE_ACTIVITIES[f.type];
    if (!activity) continue;

    // Check for active states that suggest recent interaction
    if (f.state) {
      const s = f.state as Record<string, unknown>;
      if (s.on === true || s.lit === true) activities.push(activity);
      if (typeof s.waterLevel === "number" && s.waterLevel > 0.5) activities.push(activity);
      if (typeof s.playing === "boolean" && s.playing) activities.push(activity);
    }
  }

  // Deduplicate and cap
  return [...new Set(activities)].slice(0, 5);
}
